import { DAY_INDICES, NO_RANGE_FOR_DAY } from './constants';
import type {
  DayIndex,
  DayParts,
  GetScheduleConflictLabelsOptions,
  ServiceDisplayDayPart,
  TimeRange,
} from './types';

export type {
  DayIndex,
  DayParts,
  GetScheduleConflictLabelsOptions,
  ServiceDisplayDayPart,
  TimeRange,
} from './types';

/** Extract all time ranges for a given day from a dayParts record */
export function getRangesForDay(dayParts: DayParts, day: DayIndex): TimeRange[] {
  const ranges = dayParts[day] ?? [];

  if (ranges.length > 1) {
    throw new Error(
      `Expected at most 1 time range for day "${day}", but found ${ranges.length}. Multiple ranges per day are not supported.`,
    );
  }

  return ranges;
}

/** Fixed UTC reference date so abstract clock times are not shifted by local DST. */
const ABSTRACT_TIME_REFERENCE_UTC = Date.UTC(2000, 0, 1);

/**
 * Format HH:MM (24h) for display using the caller's locale. With no `locale`
 * argument it follows the runtime's system/browser locale, matching the
 * formatting native `<input type="time">` uses for the same user. Tests pass
 * an explicit locale to keep expectations stable.
 *
 * Uses a fixed UTC instant (not local `setHours`) so spring-forward gaps
 * (e.g. 2:00–2:59 AM) are not silently shifted when formatting.
 */
export function formatTimeForDisplay(hhmm: string, locale?: string): string {
  const [hStr, mStr] = hhmm.split(':');
  const h = parseInt(hStr ?? '0', 10);
  const m = parseInt(mStr ?? '0', 10);
  const date = new Date(ABSTRACT_TIME_REFERENCE_UTC + (h * 60 + m) * 60_000);
  return date.toLocaleTimeString(locale, { hour: 'numeric', minute: '2-digit', timeZone: 'UTC' });
}

/** Format an hour (0–23) for grid axis labels (hour only, locale-aware). */
export function formatHourForDisplay(hour: number, locale?: string): string {
  const date = new Date(ABSTRACT_TIME_REFERENCE_UTC + hour * 3_600_000);
  return date.toLocaleTimeString(locale, { hour: 'numeric', timeZone: 'UTC' });
}

/** Parse "HH:MM" to minutes since midnight. O(1). */
export function hhmmToMinutes(hhmm: string): number {
  const [h = '0', m = '0'] = hhmm.split(':');
  return parseInt(h, 10) * 60 + parseInt(m, 10);
}

/** True iff the two ranges overlap on the same day (touch counts as no overlap: endA <= startB => no overlap). O(1). */
export function rangesOverlap(a: TimeRange, b: TimeRange): boolean {
  const startA = hhmmToMinutes(a.start);
  const endA = hhmmToMinutes(a.end);
  const startB = hhmmToMinutes(b.start);
  const endB = hhmmToMinutes(b.end);
  return startA < endB && startB < endA;
}

export interface DayPartOverlapLayout {
  /** Zero-based lane when multiple ranges overlap (same time column). */
  columnIndex: number;
  /** Max simultaneous overlapping ranges in this item’s overlap component (≥1). */
  maxConcurrent: number;
}

function maxConcurrentIntervals(ranges: readonly TimeRange[]): number {
  type SweepPoint = { t: number; delta: 1 | -1 };
  const points: SweepPoint[] = [];
  for (const r of ranges) {
    points.push({ t: hhmmToMinutes(r.start), delta: 1 });
    points.push({ t: hhmmToMinutes(r.end), delta: -1 });
  }
  points.sort((a, b) => (a.t === b.t ? a.delta - b.delta : a.t - b.t));
  let running = 0;
  let max = 0;
  for (const p of points) {
    running += p.delta;
    max = Math.max(max, running);
  }
  return max;
}

/**
 * For one calendar day column, assigns each day-part range a horizontal lane when ranges overlap.
 * Uses 20% of column width per lane; width shrinks by (maxConcurrent - 1) × 20% so stacked cards stay within the column.
 */
export function computeDayPartOverlapLayout(
  items: readonly { id: string; range: TimeRange }[],
): Map<string, DayPartOverlapLayout> {
  const result = new Map<string, DayPartOverlapLayout>();
  if (items.length === 0) return result;

  // Build undirected, adjacency graph between ranges - two ranges are connected if they overlap
  const adj: number[][] = Array.from({ length: items.length }, () => []);
  for (let i = 0; i < items.length; i++) {
    for (let j = i + 1; j < items.length; j++) {
      if (rangesOverlap(items[i].range, items[j].range)) {
        adj[i].push(j);
        adj[j].push(i);
      }
    }
  }

  const visited = new Array(items.length).fill(false);
  const components: number[][] = [];

  // depth-first traversal of the graph:
  // find all the ranges that are connected directly or indirectly and group them into components
  for (let itemPtr = 0; itemPtr < items.length; itemPtr++) {
    if (visited[itemPtr]) continue;
    const comp: number[] = [];
    const stack = [itemPtr];
    visited[itemPtr] = true;

    while (stack.length > 0) {
      const nodePtr = stack.pop()!;
      comp.push(nodePtr);

      for (const adjNodePtr of adj[nodePtr]) {
        if (!visited[adjNodePtr]) {
          visited[adjNodePtr] = true;
          stack.push(adjNodePtr);
        }
      }
    }
    components.push(comp);
  }

  // assign a column index to each range in the component in time order
  for (const comp of components) {
    const rangesInComp = comp.map((idx) => items[idx].range);
    const maxConcurrent = maxConcurrentIntervals(rangesInComp);

    const order = [...comp].sort(
      (a, b) =>
        hhmmToMinutes(items[a].range.start) - hhmmToMinutes(items[b].range.start) ||
        hhmmToMinutes(items[b].range.end) - hhmmToMinutes(items[a].range.end),
    );

    const active: { startMin: number; endMin: number; col: number; id: string }[] = [];

    for (const idx of order) {
      const e = items[idx];
      const startMin = hhmmToMinutes(e.range.start);
      const endMin = hhmmToMinutes(e.range.end);

      const stillActive = active.filter((a) => a.endMin > startMin);
      active.length = 0;
      for (const a of stillActive) active.push(a);

      const used = new Set(active.map((a) => a.col));
      let col = 0;
      while (used.has(col)) col += 1;

      active.push({ startMin, endMin, col, id: e.id });
      result.set(e.id, { columnIndex: col, maxConcurrent });
    }
  }

  return result;
}

const getDayPartOverlapLaneStepPercent = (maxConcurrent: number): number => {
  if (maxConcurrent <= 1) return 0;
  if (maxConcurrent <= 4) return 20;
  if (maxConcurrent <= 9) return 10;
  return 100 / (maxConcurrent + 1);
};

/**
 * Width percent for a staggered card given max concurrent lanes.
 * We always want to have a wider card than the width of a 'lane' so the cards overlap each other
 */
export function staggeredDayPartWidthPercent(maxConcurrent: number): number {
  if (maxConcurrent <= 1) return 100;
  return 100 - (maxConcurrent - 1) * getDayPartOverlapLaneStepPercent(maxConcurrent);
}

/**
 * Left offset percent for a lane.
 */
export function staggeredDayPartLeftPercent(columnIndex: number, maxConcurrent: number): number {
  if (maxConcurrent <= 1) return 0;
  return columnIndex * getDayPartOverlapLaneStepPercent(maxConcurrent);
}

/**
 * Get a human-readable time range for a given day from dayParts. Time format
 * follows the runtime locale (e.g. "6:00 AM - 11:00 AM" in en-US, "06:00 -
 * 11:00" in en-GB). Returns NO_RANGE_FOR_DAY if no range.
 */
export function formatTimeRangeForDay(dayParts: DayParts, day: DayIndex, locale?: string): string {
  const ranges = getRangesForDay(dayParts, day);
  if (ranges.length === 0) return NO_RANGE_FOR_DAY;
  const { start, end } = ranges[0];
  return `${formatTimeForDisplay(start, locale)} - ${formatTimeForDisplay(end, locale)}`;
}

/**
 * Returns labels of day parts (from otherDayParts) whose schedule overlaps the candidate on any common day.
 * Used to detect conflicts when updating a day part schedule or when enabling day parts in the widget threshold UI.
 */
export function getScheduleConflictLabels(
  candidateSchedule: DayParts,
  otherDayParts: ServiceDisplayDayPart[],
  options?: GetScheduleConflictLabelsOptions,
): string[] {
  const conflictLabels = new Set<string>();
  for (const day of DAY_INDICES) {
    const candidateRanges = getRangesForDay(candidateSchedule, day);
    const candidateRange = candidateRanges[0] ?? null;
    if (candidateRange === null) continue;
    for (const other of otherDayParts) {
      if (options?.excludeDayPartId !== undefined && other.id === options.excludeDayPartId)
        continue;
      if (
        options?.onlyIncludeDayPartIds !== undefined &&
        !options.onlyIncludeDayPartIds.has(other.id)
      )
        continue;
      const otherRanges = getRangesForDay(other.dayParts, day);
      if (otherRanges.length === 0) continue;
      if (rangesOverlap(candidateRange, otherRanges[0])) {
        conflictLabels.add(other.label);
      }
    }
  }
  return Array.from(conflictLabels);
}
